import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
// @ts-expect-error it is exported
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import toast from 'react-hot-toast';
import { useWindowWidth } from '@react-hook/window-size';
import { useMediaPredicate } from 'react-media-hook';

import Icon from 'components/icon/Icon';
import { Button } from 'components/ui/button';
import { useInitiateVoiceScan } from 'services/voice-scan/hooks';
import { encodeWAV } from 'lib/voice';
import { useVoiceScanStore } from 'store/voice-scan';

const MAX_RECORDING_TIME = 40000; // in milliseconds
const MIN_RECORDING_TIME = 35000; // in milliseconds
const RECORDING_CHECK_BEFORE = 5; // in seconds

// function getRemainingTime(time: number): number {
//   const remainingMs = MAX_RECORDING_TIME - time;
//   const remainingSec = remainingMs / 1000;

//   return remainingSec > 0 ? Math.round(remainingSec) : 0;
// }

export default function VoiceRecorder({
  setIsCompleted,
  isReset,
}: {
  setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  isReset: boolean;
}) {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');
  const isMediaTab = useMediaPredicate('(max-width: 1023px)');
  const width = useWindowWidth();

  const {
    isRecording,
    setIsRecording,
    imageData,
    setSessionId,
    recordingsRef,
    setRecordedWavesurfer,
    setAudioMetaData,
    setRecordingStartTime,
    setDuration,
    recordedWavesurfer,
  } = useVoiceScanStore();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [recordedTime, setRecordedTime] = useState<number>(0);

  const micRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const recordPluginRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(
    null
  );
  const intervalRef = useRef<number | null>(null);

  const { mutate: initiateSession } = useInitiateVoiceScan({
    onSuccess: (sessionDetail) => {
      setSessionId(sessionDetail?.session_id);
    },
  });

  const initiateRefs = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    const ws = WaveSurfer.create({
      container: micRef.current!,
      waveColor: '#CFCFCF',
      progressColor: '#007AFF',
      height: isMediaMobile ? 70 : isMediaTab ? 80 : 60,
      barWidth: 3,
      barRadius: 5,
      cursorWidth: 0,
      hideScrollbar: true,
    });

    const record = ws.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: false,
        scrollingWaveform: false,
        continuousWaveform: true,
        continuousWaveformDuration: 30,
        mediaRecorderTimeslice: 5000,
      })
    );

    wavesurferRef.current = ws;
    recordPluginRef.current = record;

    return { ws, record };
  };
  useEffect(() => {
    if (!micRef.current) return;

    const { ws, record } = initiateRefs();

    record.on('record-progress', (time: number) => {
      setRecordedTime(time);
    });

    record.on('record-end', async (blob: Blob) => {
      if (!recordingsRef.current) return;

      const recordedUrl = URL.createObjectURL(blob);
      const recordedWavesurfer = WaveSurfer.create({
        container: recordingsRef.current!,
        waveColor: '#CFCFCF',
        progressColor: '#007AFF',
        height: 40,
        barWidth: 3,
        barRadius: 5,
        url: recordedUrl,
        dragToSeek: true,
      });

      const arrayBuffer = await blob.arrayBuffer();
      const audioCtx = new AudioContext();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      let wavBlob: Blob = encodeWAV(audioBuffer);

      const fileSize = wavBlob.size;
      const fileSizeInMB = (wavBlob.size / (1024 * 1024)).toFixed(2);

      setAudioMetaData({ wav: wavBlob, fileSizeInMB, fileSize });
      setRecordedWavesurfer(recordedWavesurfer);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      record.destroy();
      ws.destroy();
    };
  }, []);

  useEffect(() => {
    RecordPlugin.getAvailableAudioDevices()
      .then((devices: MediaDeviceInfo[]) => {
        const audioDevices = devices.filter(
          (device) => device.kind === 'audioinput'
        );
        if (audioDevices.length > 0) {
          setSelectedDeviceId(audioDevices[0].deviceId);
        }
      })
      .catch((err: any) => {
        console.error('Error getting audio devices:', err);
      });
  }, []);

  useEffect(() => {
    if (isReset) {
      if (recordedWavesurfer) {
        recordedWavesurfer.destroy();
        setRecordedWavesurfer(undefined);
      }
      if (recordingsRef.current) {
        recordingsRef.current.innerHTML = ''; // 🔥 clear immediately
      }
      setAudioMetaData(undefined);
      setDuration(0);
      setRecordedTime(0);
      setIsRecording(false);

      if (recordPluginRef.current?.isRecording()) {
        recordPluginRef.current.stopRecording();
      }

      if (!micRef.current) return;

      const { ws, record } = initiateRefs();

      record.on('record-progress', (time: number) => {
        setRecordedTime(time);
      });

      record.on('record-end', async (blob: Blob) => {
        if (!recordingsRef.current) return;

        // 🔥 DESTROY EXISTING RECORDED WAVESURFER FIRST
        const existing = recordedWavesurfer;
        if (existing) {
          existing.destroy();
        }

        const recordedUrl = URL.createObjectURL(blob);
        const newRecordedWavesurfer = WaveSurfer.create({
          container: recordingsRef.current!,
          waveColor: '#CFCFCF',
          progressColor: '#007AFF',
          height: 40,
          barWidth: 3,
          barRadius: 5,
          url: recordedUrl,
          dragToSeek: true,
        });

        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new AudioContext();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        let wavBlob: Blob = encodeWAV(audioBuffer);

        const fileSize = wavBlob.size;
        const fileSizeInMB = (wavBlob.size / (1024 * 1024)).toFixed(2);

        setAudioMetaData({ wav: wavBlob, fileSizeInMB, fileSize });
        setRecordedWavesurfer(newRecordedWavesurfer);
      });

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        record.destroy();
        ws.destroy();
      };
    }
  }, [isReset]);

  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err: any) {
      console.error('Microphone permission error:', err);
      toast.error(err?.message || 'Permission denied');
      return false;
    }
  };

  const startRecording = async () => {
    if (!recordPluginRef.current) return;

    const micPermission = await requestMicPermission();
    if (!micPermission) return;

    try {
      await recordPluginRef.current.startRecording({
        deviceId: selectedDeviceId,
      });
      initiateSession({ imageId: imageData?.image_id ?? '' });
      setIsRecording(true);
      setRecordedTime(0);
      setRecordingStartTime(new Date());

      intervalRef.current = window.setInterval(() => {
        setRecordedTime((prev) => prev + 1000);
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording', err);
    }
  };

  const stopRecording = () => {
    if (recordPluginRef.current) {
      recordPluginRef.current.stopRecording();
    }
    setIsRecording(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setDuration(Math.round(recordedTime / 1000));
    setIsCompleted(true);
  };

  useEffect(() => {
    if (recordedTime >= MAX_RECORDING_TIME) {
      stopRecording();
    }
  }, [recordedTime]);

  const recordedSeconds = Math.floor(recordedTime / 1000);
  const minRecordingSec = Math.floor(MIN_RECORDING_TIME / 1000); // 35
  const maxRecordingSec = Math.floor(MAX_RECORDING_TIME / 1000); // 40

  const remainingSecs = Math.max(0, maxRecordingSec - recordedSeconds);

  return (
    <div className='grid gap-2'>
      <div
        id='mic'
        ref={micRef}
        style={{
          width: isMediaMobile ? width - 60 : '50%',
          marginInline: 'auto',
        }}
      />

      <div className='w-full mx-auto mb-4 md:w-1/2'>
        <div className='w-1/2 mx-auto md:w-full'>
          {isRecording ? (
            recordedSeconds >= minRecordingSec ? (
              // STOP BUTTON
              <>
                <p className='mb-2 text-center text-primary'>
                  {t('voice_scan_stop_message')} {remainingSecs} {t('sec')}
                </p>
                <Button className='w-full' onClick={stopRecording}>
                  <span className='font-medium'>{t('stop_recording')}</span>
                </Button>
              </>
            ) : (
              // COUNTDOWN BUTTON
              <Button
                variant='outline'
                className='w-full border-primary text-primary hover:text-primary/90 hover:bg-transparent'
                disabled
              >
                {remainingSecs - RECORDING_CHECK_BEFORE} {t('secs_remaining')}
              </Button>
            )
          ) : (
            // START BUTTON
            <Button className='w-full lg:h-10 h-9' onClick={startRecording}>
              <Icon icon='mic' size={isMediaMobile ? 19 : 20} color='#fff' />
              <span className='pt-0.5 ml-2 font-medium'>{t('start_scan')}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
